import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../../components/Navbar';
import Footer from '../../../components/Footer';
import { HiCheckCircle, HiOutlineReceiptTax, HiOutlineCalendar, HiOutlineLocationMarker, 
  HiOutlineTruck, HiArrowRight, HiOutlineMail, HiExclamation } from 'react-icons/hi';
import { fetchOrderById } from '../../../pages/admin/orders-controller/order-controller';

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFoundError, setNotFoundError] = useState(false);
  
  // Extract order details from location state if available
  useEffect(() => {
    if (location.state?.orderDetails) {
      setOrder(location.state.orderDetails);
      setLoading(false);
    } else if (orderId) {
      // Fetch order details if not provided in location state
      const fetchOrderDetails = async () => {
        try {
          setLoading(true);
          // Call the actual API
          const response = await fetchOrderById(orderId);
          
          if (!response || response.error) {
            throw new Error(response?.error || 'Failed to load order details');
          }
          
          setOrder(response);
          setLoading(false);
        } catch (err) {
          console.error('Error fetching order:', err);
          if (err.message.includes('404') || err.message.includes('not found')) {
            setNotFoundError(true);
          }
          setError('Unable to load your order details. Please check your email for confirmation.');
          setLoading(false);
        }
      };
      
      fetchOrderDetails();
    } else {
      setError('No order information provided');
      setLoading(false);
    }
  }, [orderId, location.state]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  // Get city name from code
  const getCityNameByCode = (code) => {
    const tunisianCities = {
      "TUN": "Tunis",
      "SFX": "Sfax",
      "SOS": "Sousse",
      "KRN": "Kairouan",
      "BIZ": "Bizerte",
      "GBS": "Gabes",
      "AM": "Ariana",
      "GFS": "Gafsa",
      "MNS": "Monastir",
      "BA": "Ben Arous",
      "KSR": "Kasserine",
      "MDN": "Medenine",
      "NAB": "Nabeul",
      "TAN": "Tataouine",
      "BJA": "Beja",
      "JDB": "Jendouba",
      "KEF": "Kef",
      "MHD": "Mahdia",
      "SBZ": "Sidi Bouzid",
      "TZR": "Tozeur",
      "SLI": "Siliana",
      "KBL": "Kebili",
      "ZAG": "Zaghouan",
      "MAN": "Manouba",
      "Libya": "Libya"
    };
    
    return tunisianCities[code] || code;
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.2,
        duration: 0.5
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  // Render error state
  const renderErrorState = () => (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-3xl mx-auto"
    >
      <motion.div variants={itemVariants} className="bg-red-500 px-8 py-12 text-center">
        <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm mb-6">
          <HiExclamation className="h-12 w-12 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-3">
          {notFoundError ? "Commande introuvable" : "Erreur de chargement"}
        </h1>
        <p className="text-white/90 text-lg">
          {notFoundError 
            ? "La commande que vous recherchez n'existe pas ou a peut-être été supprimée."
            : "Nous n'avons pas pu récupérer les détails de votre commande. Veuillez réessayer."}
        </p>
      </motion.div>
      
      <div className="px-8 py-10">
        <motion.div variants={itemVariants} className="text-center">
          <p className="text-gray-600 mb-8">
            {notFoundError 
              ? "Vérifiez le numéro de commande et réessayez, ou contactez notre service client pour obtenir de l'aide."
              : "Il y a eu un problème lors de la récupération des détails de votre commande. Vous pouvez réessayer ou contacter notre service client."}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link
              to="/"
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
            >
              Retour à l'accueil
            </Link>
            
            <button
              onClick={() => navigate('/catalog')}
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
            >
              Explorer nos produits
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
  
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      
      <main className="flex-grow pt-32 pb-16">
        <div className="container mx-auto px-4 max-w-5xl">
          {loading ? (
            <div className="flex justify-center items-center py-32">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#174540]"></div>
            </div>
          ) : error ? (
            renderErrorState()
          ) : order ? (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="bg-white rounded-2xl shadow-xl overflow-hidden"
            >
              {/* Success Header */}
              <motion.div variants={itemVariants} className="bg-gradient-to-r from-[#174540] to-[#297D74] px-8 py-12 text-center">
                <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm mb-6">
                  <HiCheckCircle className="h-12 w-12 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-3">Commande Confirmée!</h1>
                <p className="text-white/90 text-lg">
                  Merci pour votre commande.
                </p>
              </motion.div>
              
              {/* Order Info */}
              
            </motion.div>
          ) : (
            renderErrorState() // Also show error state if order is null but not loading
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default OrderConfirmation;